/**
 * Task Attachments API Integration
 */

import axiosClient from '../axiosClient';
import {
  TaskAttachment,
  UpdateAttachmentDto,
  AttachmentCountResponse,
} from '../types/AttachmentTypes';

export const attachmentsApi = {
  /**
   * Upload an attachment to a task
   */
  uploadAttachment: async (
    taskId: string,
    file: File,
    description?: string,
  ): Promise<TaskAttachment> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const response = await axiosClient.post(
        `/tasks/${taskId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      console.log('✅ Attachment uploaded to task:', taskId);
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading attachment:', error);
      throw error;
    }
  },

  /**
   * Get all attachments for a task
   */
  getTaskAttachments: async (taskId: string): Promise<TaskAttachment[]> => {
    try {
      const response = await axiosClient.get(`/tasks/${taskId}/attachments`);
      console.log('✅ Task attachments fetched:', taskId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching task attachments:', error);
      throw error;
    }
  },

  /**
   * Get attachment count for a task
   */
  getAttachmentCount: async (taskId: string): Promise<number> => {
    try {
      const response = await axiosClient.get<AttachmentCountResponse>(
        `/tasks/${taskId}/attachments/count`,
      );
      console.log('✅ Attachment count fetched:', taskId);
      return response.data.count;
    } catch (error) {
      console.error('❌ Error fetching attachment count:', error);
      throw error;
    }
  },

  /**
   * Download an attachment
   */
  downloadAttachment: async (
    taskId: string,
    attachmentId: string,
  ): Promise<Blob> => {
    try {
      const response = await axiosClient.get(
        `/tasks/${taskId}/attachments/${attachmentId}/download`,
        {
          responseType: 'blob',
        },
      );
      console.log('✅ Attachment downloaded:', attachmentId);
      return response.data;
    } catch (error) {
      console.error('❌ Error downloading attachment:', error);
      throw error;
    }
  },

  /**
   * Update attachment metadata
   */
  updateAttachment: async (
    taskId: string,
    attachmentId: string,
    data: UpdateAttachmentDto,
  ): Promise<TaskAttachment> => {
    try {
      const response = await axiosClient.put(
        `/tasks/${taskId}/attachments/${attachmentId}`,
        data,
      );
      console.log('✅ Attachment updated:', attachmentId);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating attachment:', error);
      throw error;
    }
  },

  /**
   * Delete an attachment
   */
  deleteAttachment: async (
    taskId: string,
    attachmentId: string,
  ): Promise<void> => {
    try {
      await axiosClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
      console.log('✅ Attachment deleted:', attachmentId);
    } catch (error) {
      console.error('❌ Error deleting attachment:', error);
      throw error;
    }
  },
};

export default attachmentsApi;
